import {
  eolsBaseApi,
  withApiErrorLoggingOnly
} from '../../utilities/api.utilities';
import { addSearchParams } from '../../utilities/app.utilities';
import {
  RegenerateResponseDto,
  EbookSectionSsoRequestDto,
  EbookReadingRequestDto,
  EbookReadingResponseDto,
  LlmPromptDto,
  LlmResponseDto,
  NewQuizSessionDto,
  RaasAssessmentDto,
  RaasAssessmentPaginationDto,
  RaasAssessmentWithAssignmentDto,
  QuestionSubmissionDetailsDto,
  QuizAttemptDetailsDto,
  QuizSessionDto,
  RecommendationAttemptDto,
  RecommendationAttemptSearchRequestDto,
  RecommendationDto,
  RecommendationGenerationStatusDto,
  RecommendationItemDto,
  RemediationAssignmentDto,
  RemediationAssignmentWithSourcesDto,
  VectorSearchDto,
  VectorSearchResponseDto,
  HesiExamInfoDto,
  RaasAssessmentSourceDto,
  RaasAssessmentSourceTypeDto,
  RaasAssessmentMetricsDto,
  RecommendationFeedbackDto,
  SherpathAIContextDto,
  RecommendationSourceDto,
} from './nhe-remediation-service.dtos';
import {
  AssessmentSubmissionDto
} from '../eols-assessment-service/eols-assessment-service.dtos';

export const fetchRaasAssessment = (props: {
  id: number;
  studentStudyEolsAssessmentId: number;
}): Promise<RaasAssessmentDto> => {
  const {
    id,
    studentStudyEolsAssessmentId
  } = props;
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(addSearchParams(
      '/remediation/v1/raas/assessment',
      { id, studentStudyEolsAssessmentId }
    ));
  });
};

export const fetchMyRaasAssessments = (): Promise<RaasAssessmentWithAssignmentDto[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get('/remediation/v1/raas/assessments/mine');
  });
};

export const fetchRecommendations = (studentStudyEolsAssessmentId?: number, id?: number[]): Promise<RecommendationDto[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(addSearchParams(
      '/remediation/v1/recommendation',
      { studentStudyEolsAssessmentId, id }
    ));
  });
};

export const putRecommendation = (recommendation: RecommendationDto): Promise<RecommendationDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.put(
      addSearchParams(
        '/remediation/v1/recommendation',
        {}
      ),
      { data: recommendation }
    );
  });
};

export const putRaaSAssessment = (raasAssessment: RaasAssessmentDto): Promise<RaasAssessmentDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.put(
      addSearchParams(
        '/remediation/v1/admin/raas/assessment',
        {}
      ),
      { data: raasAssessment }
    );
  });
};

const remediationAttemptPath = '/remediation/v1/recommendation/attempt';

export const fetchRecommendationAttempts = (searchRequest: RecommendationAttemptSearchRequestDto): Promise<RecommendationAttemptDto[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(`${remediationAttemptPath}/search`, { data: searchRequest });
  });
};

export const postRecommendationAttempt = (attempt: Partial<RecommendationAttemptDto>): Promise<RecommendationAttemptDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(addSearchParams(remediationAttemptPath, {}), { data: attempt });
  });
};

export const putRecommendationAttempt = (attempt: RecommendationAttemptDto): Promise<RecommendationAttemptDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.put(addSearchParams('/remediation/v1/recommendation/attempt', {}), { data: attempt });
  });
};

export const fetchRemediationAssignment = (remediationAssignmentId: number): Promise<RemediationAssignmentDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(addSearchParams(`/remediation/v1/assignment/${remediationAssignmentId}`, null));
  });
};

export const fetchRemediationAssignmentWithSources = (remediationAssignmentId: number): Promise<RemediationAssignmentWithSourcesDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(addSearchParams('/remediation/v1/assignment-with-sources', {
      remediationAssignmentId
    }));
  });
};

export const fetchRecommendationItems = (recommendationId: number): Promise<RecommendationItemDto[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(addSearchParams('/remediation/v1/recommendation/items', { recommendationId }));
  });
};

export const fetchRecommendationSources = (props: {
  recommendationId?: number[];
  id?: number[];
}): Promise<RecommendationSourceDto[]> => {
  const { recommendationId, id } = props;
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(addSearchParams('/remediation/v1/recommendation/source', { recommendationId, id }));
  });
};

export const adminSearchVector = (body: VectorSearchDto): Promise<VectorSearchResponseDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post('/remediation/v1/admin/hm-platform/search/vector', { data: body });
  });
};

export const adminPrompt = (body: LlmPromptDto): Promise<LlmResponseDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post('/remediation/v1/admin/hm-platform/prompt', { data: body });
  });
};

export const postRaaSQuizSession = (quizSessionDto: NewQuizSessionDto): Promise<QuizSessionDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post('/remediation/v1/quiz-session', { data: quizSessionDto });
  });
};

export const fetchQuizSessionById = (quizSessionId: number): Promise<QuestionSubmissionDetailsDto[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(addSearchParams('/remediation/v1/quiz-session/question/submissions', { quizSessionId }));
  });
};

export const fetchQuizSessionByRecommendationAttemptId = (recommendationAttemptId: number): Promise<QuizAttemptDetailsDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(addSearchParams('/remediation/v1/quiz-session/question/submissions', { recommendationAttemptId }));
  });
};

export const fetchEbookReadingSsoUrl = (body: EbookReadingRequestDto): Promise<EbookReadingResponseDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post('/remediation/v1/ebook/sso-url', {
      data: body
    });
  });
};

export const fetchEbookSectionSsoUrl = (body: EbookSectionSsoRequestDto): Promise<EbookReadingResponseDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post('/remediation/v1/ebook/sso-url/assertion', {
      data: body
    });
  });
};

export const fetchRaasAssessments = (request: {
  status: RecommendationGenerationStatusDto[];
  userId: number[];
  id: number[];
  remediationAssignmentId: number[];
  studentStudyEolsAssessmentId: number[];
  courseSectionId: number[];
  environment: string[];
  sourceType: RaasAssessmentSourceTypeDto[];
  createdAtFrom: string;
  createdAtTo: string;
  hasRegeneratedToId: boolean;
  hasRegeneratedFromId: boolean;
  isActive: boolean;
  page: number;
  pageSize: number;
}): Promise<RaasAssessmentPaginationDto> => {
  const {
    id,
    userId,
    remediationAssignmentId,
    studentStudyEolsAssessmentId,
    courseSectionId,
    environment,
    sourceType,
    createdAtFrom,
    createdAtTo,
    hasRegeneratedToId,
    hasRegeneratedFromId,
    isActive,
    status,
    page,
    pageSize
  } = request;
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(addSearchParams('/remediation/v1/admin/raas/assessment', {
      id,
      status,
      userId,
      remediationAssignmentId,
      studentStudyEolsAssessmentId,
      courseSectionId,
      environment,
      sourceType,
      createdAtFrom,
      createdAtTo,
      hasRegeneratedToId,
      hasRegeneratedFromId,
      isActive,
      page,
      pageSize
    }));
  });
};

export const regenerateAssessmentRecommendations = (raasAssessmentIds: number[]): Promise<RegenerateResponseDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(addSearchParams('/remediation/v1/admin/raas/assessment/regenerate', {
      raasAssessmentIds
    }));
  });
};

export const submitReviewAttempt = (
  recommendationAttemptId: number,
  assessmentSubmission: Partial<AssessmentSubmissionDto>
): Promise<RecommendationAttemptDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(addSearchParams('/remediation/v1/review/attempt/submission', {
      recommendationAttemptId
    }), {
      data: assessmentSubmission
    });
  });
};

export const copyRaasAssessment = (props: { raasAssessmentId: number; newUserId: number }): Promise<RaasAssessmentDto> => {
  const { raasAssessmentId, newUserId } = props;
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(addSearchParams('/remediation/v1/admin/raas/assessment/clone', {
      raasAssessmentId,
      newUserId
    }));
  });
};

export const fetchAssessmentsByRemediationAssignmentId = (remediationAssignmentId: number): Promise<RaasAssessmentDto[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(addSearchParams('/remediation/v1/raas/assessments', { remediationAssignmentId }));
  });
};

export const fetchHesiExamInfoByExamEditionId = (editionId: number): Promise<HesiExamInfoDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(addSearchParams('/remediation/v1/hesi/exam', { editionId }));
  });
};

export const patchRaasAssessmentLastAccessed = (raasAssessmentId: number): Promise<RaasAssessmentDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.patch(addSearchParams('/remediation/v1/raas/assessment/access', { id: raasAssessmentId }));
  });
}

export const fetchRaasAssessmentSource = (raasAssessmentId: number): Promise<RaasAssessmentSourceDto[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(addSearchParams('/remediation/v1/raas/assessment/source', { raasAssessmentId }));
  });
}

export const fetchAssessmentMetrics = (eolsAssessmentId: number): Promise<RaasAssessmentMetricsDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(`/remediation/v1/assessment/${eolsAssessmentId}/metrics`);
  });
};

export const fetchSherpathAIContext = (plpAssignmentId: string): Promise<SherpathAIContextDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get<SherpathAIContextDto>(`/remediation/v1/assignment/${plpAssignmentId}/sherpath-ai-context`);
  });
};

export const fetchRecommendationFeedback = (recommendationId: number): Promise<RecommendationFeedbackDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get(addSearchParams('/remediation/v1/recommendation/feedback', { recommendationId }));
  });
};

export const postRecommendationFeedback = (dto: RecommendationFeedbackDto): Promise<void> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post('/remediation/v1/recommendation/feedback', { data: dto });
  });
};

export const patchRecommendationFeedback = (dto: RecommendationFeedbackDto): Promise<void> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.patch('/remediation/v1/recommendation/feedback', { data: dto });
  });
};
